import api from "api"
import { AuthData } from "api/resources/auth/types"
import { transformKeys } from "api/utils"
import camelCase from "lodash/camelCase"
import routes from "routes"
import { BROWSER_STORAGE_KEYS, Platforms, USER_TYPE } from "utils/constants"
import { getPlatformURL } from "utils/helpers"
import { USER_PREFS_KEY_NAME } from "utils/userPreferenceManager"

/**
 * Don't call this function directly because it doesn't clear the localStorage.
 * To actually implement the sign out functionality, use `redirectToLogout` instead.
 */
const getSignOutLink = () => {
  const url = new URL(getPlatformURL("sso", "/logout"))
  url.searchParams.set("platform", USER_TYPE)

  // If this function was triggered from the logout page, don't set it to redirect-url
  // Because that would just bring the user back to /logout if they decide to login again.
  if (window.location.pathname === routes.logout) {
    url.searchParams.set("redirect-url", window.location.origin)
  } else {
    const currentURL = new URL(window.location.href)
    currentURL.searchParams.delete("code")
    url.searchParams.set("redirect-url", currentURL.toString())
  }

  return url.toString()
}

export const AuthManager = {
  isLoggedIn(platform: Platforms = USER_TYPE) {
    const auth = this.getPlatformAuth(platform)
    return Boolean(auth && auth.accessToken)
  },

  async verifyToken(platform: Platforms = USER_TYPE) {
    const auth = this.getPlatformAuth(platform)
    if (!auth) return false
    try {
      const res = await api.auth.verifyAccessToken({
        data: { token: auth.accessToken },
        headers: { Authorization: null },
      })
      return !!res.verified
    } catch {
      return false
    }
  },

  async processAuthCode(
    code: string,
    platform: Platforms = USER_TYPE,
    shadow = false
  ) {
    try {
      const response = await api.auth.getUserToken({
        data: { code, platform },
      })
      const auth: AuthData = {
        ...response,
        generatedAt: Date.now(),
        expiresAt: Date.now() + response.expiresIn * 1000,
        // Keep the key off normal auth objects entirely
        ...(shadow && { shadow: true }),
      }
      this.setPlatformAuth(auth, platform)
      return auth
    } catch (e) {
      console.error("AuthManager: processAuthCode error", e)
      return null
    }
  },

  clearAuthInfo(platform: Platforms = USER_TYPE) {
    switch (platform) {
      case Platforms.school:
        localStorage.removeItem("schoolAuth")
        break
      case Platforms.recruiter:
        localStorage.removeItem("recruiterAuth")
        break
      case Platforms.centerAdmin:
        localStorage.removeItem("centreAdminAuth")
        break
      default: {
        const AllKeys = BROWSER_STORAGE_KEYS
        const keysToNotRemove = [AllKeys.language, USER_PREFS_KEY_NAME]

        Object.entries(AllKeys).forEach(([_, value]) => {
          if (!keysToNotRemove.includes(value)) {
            localStorage.removeItem(value)
          }
        })
        sessionStorage.clear()
      }
    }
  },

  redirectToLogout() {
    this.clearAuthInfo()
    console.log("> Redirecting to logout")
    window.location.href = getSignOutLink()
  },

  redirectToLogin(platform = USER_TYPE) {
    console.log(`> Redirecting to login for: ${platform}`)
    const url = new URL(getPlatformURL("sso", "/"))
    url.searchParams.set("platform", platform)

    const currentURL = new URL(window.location.href)
    currentURL.searchParams.delete("code")

    url.searchParams.set("redirect-url", currentURL.toString())
    window.location.href = url.toString()
    // console.log(`> Login URL: ${url.toString()}`)
  },

  getPlatformAuth(platform: Platforms = USER_TYPE): AuthData | null {
    try {
      switch (platform) {
        case Platforms.school:
          return transformKeys(
            JSON.parse(localStorage.getItem("schoolAuth") || "null"),
            camelCase
          )
        case Platforms.recruiter:
          return transformKeys(
            JSON.parse(localStorage.getItem("recruiterAuth") || "null"),
            camelCase
          )
        case Platforms.centerAdmin:
          return transformKeys(
            JSON.parse(localStorage.getItem("centreAdminAuth") || "null"),
            camelCase
          )
        default:
          return transformKeys(
            JSON.parse(
              localStorage.getItem(BROWSER_STORAGE_KEYS.auth) || "null"
            ),
            camelCase
          )
      }
    } catch {
      return null
    }
  },

  setPlatformAuth(authInfo: AuthData, platform: Platforms = USER_TYPE) {
    switch (platform) {
      case Platforms.school:
        localStorage.setItem("schoolAuth", JSON.stringify(authInfo))
        break
      case Platforms.recruiter:
        localStorage.setItem("recruiterAuth", JSON.stringify(authInfo))
        break
      case Platforms.centerAdmin:
        localStorage.setItem("centreAdminAuth", JSON.stringify(authInfo))
        break
      default:
        localStorage.setItem(
          BROWSER_STORAGE_KEYS.auth,
          JSON.stringify(authInfo)
        )
        break
    }
  },
}
